import React, { useState, useEffect } from "react";
import { Tabs, Tab, Autocomplete, TextField } from "@mui/material";
import { useTable, usePagination } from "react-table";
import { CSVLink } from "react-csv";
import { Modal, Box, Typography } from "@mui/material";

const getYesterdayDate = () => {
  const yesterday = new Date(Date.now() - 86400000); // subtract 1 day
  return yesterday.toISOString().split("T")[0]; // format as YYYY-MM-DD
};

const StepForm = ({ onSubmit, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: "",
    date: getYesterdayDate(),
    steps: "",
  });
  const [nameOptions, setNameOptions] = useState([]);
  useEffect(() => {
    fetch("/api/stepchallenge/names")
      .then((res) => res.json())
      .then((data) => setNameOptions(data))
      .catch(() => {});

    const savedName = localStorage.getItem("stepchallenge-name") || "";
    if (savedName) {
      setFormData((prev) => ({ ...prev, name: savedName }));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "name") {
      localStorage.setItem("stepchallenge-name", value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.name && formData.date && formData.steps) {
      try {
        const response = await fetch("/api/stepchallenge", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        });

        if (response.ok) {
          onSubmit(formData);
          onSuccess("Entry submitted successfully!");
          setFormData({
            name: localStorage.getItem("stepchallenge-name") || "",
            date: new Date().toISOString().split("T")[0],
            steps: "",
          });
        } else {
          console.error("Failed to submit");
        }
      } catch (error) {
        console.error("Error submitting:", error);
      }
    } else {
      console.log("Validation failed:", formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 w-full">
      <Autocomplete
        freeSolo
        options={nameOptions}
        value={formData.name || ""}
        onChange={(e, value) => {
          setFormData((prev) => ({ ...prev, name: value || "" }));
          localStorage.setItem("stepchallenge-name", value || "");
        }}
        onInputChange={(e, value) => {
          setFormData((prev) => ({ ...prev, name: value || "" }));
          localStorage.setItem("stepchallenge-name", value || "");
        }}
        renderInput={(params) => (
          <TextField {...params} label="Name" variant="outlined" fullWidth />
        )}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="app-muted text-sm font-medium">Date</span>
          <input
            type="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            className="app-input w-full rounded-xl border px-3 py-3 text-base"
            required
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="app-muted text-sm font-medium">Total Steps</span>
          <input
            type="number"
            name="steps"
            placeholder="Total Steps"
            inputMode="numeric"
            value={formData.steps}
            onChange={handleChange}
            className="app-input w-full rounded-xl border px-3 py-3 text-base tabular-nums"
            required
          />
        </label>
      </div>
      <button
        type="submit"
        className="app-btn app-btn-primary app-btn-lg w-full"
      >
        Submit
      </button>
    </form>
  );
};

const DataTable = ({ columns, data }) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page, // instead of rows
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 25 },
    },
    usePagination
  );

  return (
    <div>
      <div className="app-surface overflow-hidden rounded-2xl border">
        <div className="overflow-x-auto">
          <table {...getTableProps()} className="app-table">
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                  {headerGroup.headers.map((column) => (
                    <th {...column.getHeaderProps()} key={column.id}>
                      {column.render("Header")}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={row.id}>
                    {row.cells.map((cell) => (
                      <td
                        {...cell.getCellProps()}
                        className="tabular-nums"
                        key={cell.column.id}
                      >
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm">
        <div className="tabular-nums">
          Page{" "}
          <strong>
            {pageIndex + 1} of {pageOptions.length}
          </strong>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}
            className="app-btn app-btn-secondary app-btn-sm min-w-[2.5rem]"
          >
            {"<<"}
          </button>
          <button
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
            className="app-btn app-btn-secondary app-btn-sm min-w-[2.5rem]"
          >
            {"<"}
          </button>
          <button
            onClick={() => nextPage()}
            disabled={!canNextPage}
            className="app-btn app-btn-secondary app-btn-sm min-w-[2.5rem]"
          >
            {">"}
          </button>
          <button
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
            className="app-btn app-btn-secondary app-btn-sm min-w-[2.5rem]"
          >
            {">>"}
          </button>
        </div>
        <select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          aria-label="Rows per page"
          className="app-input rounded-xl border py-1.5 pl-3 pr-8 text-sm"
        >
          {[10, 25, 50, 100].map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};
const getRankDisplay = (rank) => {
  if (typeof rank !== "number") return "-";
  switch (rank) {
    case 1:
      return "🥇";
    case 2:
      return "🥈";
    case 3:
      return "🥉";
    default:
      return rank.toString();
  }
};

export default function StepCompetition() {
  const [tab, setTab] = useState(0);
  const [entries, setEntries] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    if (tab === 1) {
      fetch("/api/stepchallenge/leaderboard")
        .then((res) => res.json())
        .then((data) => {
          const sorted = data.sort((a, b) => b.totalSteps - a.totalSteps);
          const ranked = sorted.map((item, index) => ({
            ...item,
            rank: index + 1,
          }));
          setLeaderboard(ranked);
        })
        .catch(() => setLeaderboard([]));
    } else if (tab === 2) {
      fetch("/api/stepchallenge/entries")
        .then((res) => res.json())
        .then((data) => setEntries(data))
        .catch(() => setEntries([]));
    }
  }, [tab]);

  const handleSubmit = (entry) => {
    setEntries((prev) => [...prev, entry]);
    setLeaderboard((prev) => {
      const updated = [...prev];
      const index = updated.findIndex((e) => e.name === entry.name);
      if (index !== -1) {
        updated[index].steps += parseInt(entry.steps);
      } else {
        updated.push({ name: entry.name, steps: parseInt(entry.steps) });
      }
      return updated.sort((a, b) => b.steps - a.steps);
    });
  };

  const handleSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => {
      setSuccessMessage("");
    }, 3000);
  };

  const entryColumns = [
    { Header: "Name", accessor: "name" },
    { Header: "Date", accessor: "date" },
    { Header: "Steps", accessor: "steps" },
  ];

  const leaderboardColumns = [
    {
      Header: "Rank",
      accessor: "rank",
      Cell: ({ value }) => getRankDisplay(value),
      disableSortBy: true,
      width: 60,
    },
    { Header: "Name", accessor: "_id" },
    { Header: "Total Steps", accessor: "totalSteps" },
    {
      Header: "Avg Steps/Entry",
      accessor: (row) => {
        if (!row.daysCount || row.daysCount === 0) return 0;
        return Math.round(row.totalSteps / row.daysCount);
      },
      id: "avgStepsPerDay",
    },
  ];

  const csvHeaders = [
    { label: "Name", key: "name" },
    { label: "Date", key: "date" },
    { label: "Steps", key: "steps" },
  ];

  const filename = `step-entries-${new Date().toISOString().split("T")[0]}.csv`;

  return (
    // Standalone route (no auth header, no footer), so this page owns a themed
    // full-width surface of its own; the centered column below is unchanged.
    <div className="app-page min-h-screen">
      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
        <div className="mb-6 text-center">
          <h1 className="flex items-center justify-center gap-2 text-3xl font-bold tracking-tight">
            June Step Challenge
            <span
              role="img"
              aria-label="walking"
              className="inline-block animate-walk"
            >
              🚶‍♂️
            </span>
            <span
              role="img"
              aria-label="walking"
              className="inline-block animate-walk delay-100"
            >
              🚶‍♀️
            </span>
            <span
              role="img"
              aria-label="walking"
              className="inline-block animate-walk delay-200"
            >
              🚶
            </span>
          </h1>
        </div>

        <div className="app-card w-full rounded-[1.75rem] border p-4 sm:p-6">
          <div>
            <Tabs
              value={tab}
              onChange={(e, newVal) => setTab(newVal)}
              variant="scrollable"
              scrollButtons="auto"
              className="w-full"
            >
              <Tab label="Enter Steps" className="text-sm" />
              <Tab label="Leaderboard" className="text-sm" />
              <Tab label="Entry Log" className="text-sm" />
            </Tabs>

            {successMessage && (
              <div
                role="status"
                className="my-4 rounded-xl bg-emerald-500/15 px-3 py-2.5 text-sm font-medium text-emerald-700 dark:text-emerald-300"
              >
                {successMessage}
              </div>
            )}

            <div className="mt-6 flex flex-col gap-4">
              {tab === 0 && (
                <StepForm onSubmit={handleSubmit} onSuccess={handleSuccess} />
              )}
              {tab === 1 && (
                <DataTable columns={leaderboardColumns} data={leaderboard} />
              )}
              {tab === 2 && (
                <>
                  <CSVLink
                    data={entries}
                    headers={csvHeaders}
                    filename={filename}
                    className="app-btn app-btn-secondary self-start"
                  >
                    Export Entry Log CSV
                  </CSVLink>
                  <DataTable data={entries} columns={entryColumns} />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
